import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Select,
  Spinner,
  Stack,
  Tag,
} from '@chakra-ui/react';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { ObjectivesValues } from '@common/models/InvestorAccountModel/types';
import { RiskValueMapping } from '@services/appsync/mappings';
import { SurveyV2ObjectiveChoice } from '@services/appsync/types';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

export const ObjectivesForm: React.FC = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>();
  const { investorAccount, updateObjectivesRisks } = useInvestorAccountModel();
  const survey = investorAccount?.non_accredited_survey_v2 ?? null;

  const form = useForm<ObjectivesValues>({
    defaultValues: {
      objectiveChoice: survey?.objective?.value,
      riskProfile: survey?.risk_profile?.value,
    },
  });

  const save = form.handleSubmit(async (data) => {
    setIsSaving(true);
    setFormError(null);
    const result = await updateObjectivesRisks(data);
    if (result.error) {
      form.reset();
      setIsSaving(false);
      setFormError(result.error);
    } else {
      form.reset(data);
      setIsSaving(false);
    }
  });

  const needsAttention = !survey?.objective?.value || !survey?.risk_profile?.value;

  return (
    <Grid templateColumns={['1fr 1fr']} gap={25} w="100%">
      <GridItem colSpan={2} display="flex" flexDirection="row" justifyContent="space-between">
        <Stack direction={['column', 'row']} spacing={[2, 5]}>
          <Heading id="objectives" fontSize={'21px'}>
            Objectives
          </Heading>
          {needsAttention && <Tag colorScheme={'yellow'}>Needs Attention</Tag>}
        </Stack>
        {form.formState.isDirty &&
          (isSaving ? (
            <Spinner color="blue.500" size="md" thickness="4px" />
          ) : (
            <HStack spacing={5}>
              <Button onClick={save}>Save</Button>
              <Button variant="link" onClick={() => form.reset()}>
                Cancel
              </Button>
            </HStack>
          ))}
      </GridItem>
      {formError && (
        <GridItem colSpan={2}>
          <Alert status="error">
            <AlertIcon />
            {formError}
          </Alert>
        </GridItem>
      )}
      <GridItem colSpan={2} borderTopWidth={1} borderTopColor={'gray.50'} />

      <GridItem colSpan={[2, 1]}>
        <FormControl isDisabled={isSaving} isInvalid={Boolean(form.formState.errors.objectiveChoice)}>
          <FormLabel htmlFor="employmentStatus">What is your main objective for investing with Concreit?</FormLabel>
          <Select
            isDisabled={isSaving}
            placeholder="Select option"
            {...form.register('objectiveChoice', {
              validate: (value) => {
                if (!value) {
                  return 'Your objective is required';
                } else {
                  return true;
                }
              },
            })}>
            <option value={SurveyV2ObjectiveChoice.BALANCED}>Create an additional income stream</option>
            <option value={SurveyV2ObjectiveChoice.GROWTH}>Grow my investment portfolio</option>
            <option value={SurveyV2ObjectiveChoice.PRESERVATION}>Preserve my savings with minimal risk</option>
          </Select>
          <FormErrorMessage>
            {form.formState.errors.objectiveChoice && form.formState.errors.objectiveChoice?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>

      <GridItem colSpan={[2, 1]}>
        <FormControl isDisabled={isSaving} isInvalid={Boolean(form.formState.errors.riskProfile)}>
          <FormLabel htmlFor="riskProfile">What type of investments would you be comfortable with?</FormLabel>

          <Select
            isDisabled={isSaving}
            placeholder="Select option"
            {...form.register('riskProfile', {
              validate: (value) => {
                if (!value) {
                  return 'Risk level must be specified';
                } else {
                  return true;
                }
              },
            })}>
            <option value={RiskValueMapping.HIGH}>High risk, high potential return</option>
            <option value={RiskValueMapping.MEDIUM}>Moderate risk, moderate potential return</option>
            <option value={RiskValueMapping.LOW}>Low risk, low potential return</option>
          </Select>
          <FormErrorMessage>
            {form.formState.errors.riskProfile && form.formState.errors.riskProfile?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>
    </Grid>
  );
};
